import 'lite-youtube-embed'
import 'virtual:svg-icons-register'
import 'lite-vimeo-embed/module/lite-vimeo-embed'

import { GarbanzoConfig } from '~/types/globals/Garbanzo'

import Swup, { getCurrentUrl } from 'swup'
import SwupHeadPlugin from '@swup/head-plugin'
import SwupA11yPlugin from '@swup/a11y-plugin'
import SwupDebugPlugin from '@swup/debug-plugin'
import SwupProgressPlugin from '@swup/progress-plugin'

import { createPinia } from 'pinia'
import { useUiStore } from '~/store/ui'
import { useConfigStore } from '~/store/config'
import { useUserStore } from '~/store/user'

import Hydrater from '~/services/hydrater/Hydrater'
import VueHydratable from '~/services/hydrater/hydratables/VueHydratable'
import HydrationStrategy from '~/services/hydrater/base/HydrationStrategy'

import useApp from '~/composables/useApp'
import { colorText } from './lib/color'
import { redirectToAppIfRequired } from '~/lib/redirect'
import { Formie, handleFormieLoad, initFormieForms, refreshFormieForms } from '~/composables/useFormie'
import { watch } from 'vue'

import.meta.glob('../img/**/*', { query: '?url', import: 'default', eager: true })

// Config from HTML
const GARBANZO = window.GARBANZO || {
  formieLoaded() {
    this.CONFIG.FORMIE_LOADED = true
  },
  CONFIG: {
    SENTRY: {},
    FORMIE_LOADED: false,
  } as GarbanzoConfig,
}
if (GARBANZO.CONFIG.FORMIE_LOADED) {
  // continue immediately
  handleFormieLoad(window.Formie)
} else {
  // replace the script load handler
  GARBANZO.formieLoaded = () => {
    GARBANZO.CONFIG.FORMIE_LOADED = true
    handleFormieLoad(window.Formie)
  }
}
// Globally shared pinia instance for all Vue apps
const pinia = createPinia()
// We need to initialize a Vue instance with Pinia for useUiStore to work in our Swup
// hook below. We never do anything  else with this component, it's just a dummy / kludge
useApp(pinia, {
  mountName: 'StoreManager',
})
const uiStore = useUiStore()
const configStore = useConfigStore()
configStore.setConfig(GARBANZO.CONFIG)

const userStore = useUserStore()

// Watch for changes to user login state and redirect if needed
watch(() => userStore.isLoggedInUser, (isLoggedIn) => {
  redirectToAppIfRequired(getCurrentUrl(), isLoggedIn)
})

type SwupPlugins = (SwupHeadPlugin | SwupA11yPlugin | SwupDebugPlugin | SwupProgressPlugin)[]

// @see https://swup.js.org/plugins/
const swupPlugins: SwupPlugins = [
  new SwupProgressPlugin(),
  new SwupHeadPlugin(),
  new SwupA11yPlugin(),
]

if (GARBANZO.CONFIG?.DEBUG) {
  swupPlugins.push(new SwupDebugPlugin())
}

// Page transition manager
const swup = new Swup({
  containers: ['#main', '#nav-main'],
  plugins: swupPlugins,
})

// Component Hydrater - see src/assets/js/services/hydrater/README.md for more info
const hydrater = new Hydrater([
  new HydrationStrategy({
    name: 'vue',
    hydratable: VueHydratable,
    factory: (el, { mountName }) => useApp(pinia, {
      mountName: `Hydrated${mountName || 'App'}`,
      ...GARBANZO,
      FLAGS: {
        withSentry: true,
      },
    }),
  }),
])

// Don't mount components if we have Craft / Larapack debug dump output in the page
const shouldMountComponents = !import.meta.env.DEV || typeof window?.Sfdump !== 'function'

const handlePageStart = () => {
  userStore.refreshUser()
  redirectToAppIfRequired(getCurrentUrl(), userStore.isLoggedInUser)
  hydrater.update()
  colorText()
  initFormieForms()
}

const handlePageEnd = () => {
  uiStore.resetMobileNav()
  uiStore.toggleNavDropdown()
}

if (shouldMountComponents) {
  // Mount components on page load
  window.addEventListener('DOMContentLoaded',  handlePageStart)
  // Clean up/reset Vue state when we start a transition
  swup.hooks.on('animation:out:start', handlePageEnd)
  // When we finish a transition, create and mount new components / unmount old ones
  swup.hooks.on('page:view', handlePageStart)
  swup.hooks.on('visit:end', refreshFormieForms)
}

// Expose our global state to the window object to aid debugging
window.GARBANZO = {
  ...GARBANZO,
  swup,
  hydrater,
  pinia,
}

if (import.meta.hot) {
  import.meta.hot.accept(() => {
    console.log('HMR')
  })
}
